import { DiscordSDK } from "@discord/embedded-app-sdk";
import type { Identity } from "./identity.ts";

/**
 * Inside Discord the page is loaded in an iframe from https://<client id>.discordsays.com with
 * ?frame_id=... in the URL. Anywhere else (your browser, the offline demo) it isn't.
 */
export function isDiscordActivity(): boolean {
  return new URLSearchParams(location.search).has("frame_id") && location.hostname.endsWith(".discordsays.com");
}

export interface DiscordSession {
  identity: Identity;
  /** Everyone in the same launched Activity shares this id, so they share a table. */
  tableId: string;
}

/** Handshake with Discord, then sign the player in. The client secret never touches the browser. */
export async function signInWithDiscord(): Promise<DiscordSession> {
  // The hostname is "<client id>.discordsays.com", so we don't need to bake the client id into the build.
  const clientId = location.hostname.split(".")[0]!;
  const sdk = new DiscordSDK(clientId);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: "code",
    state: "",
    prompt: "none",
    scope: ["identify"],
  });

  // Our own server swaps the one-time code for an access token (it holds the client secret).
  const res = await fetch("/api/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(`Sign-in failed at the server (${res.status})`);
  const { access_token } = (await res.json()) as { access_token: string };

  const auth = await sdk.commands.authenticate({ access_token });
  return {
    identity: { playerId: auth.user.id, name: auth.user.global_name || auth.user.username, token: access_token },
    tableId: sdk.instanceId,
  };
}
